import React, { useState } from 'react';
import {
  Container, Grid, Avatar, Box, Typography, Divider, IconButton, Tooltip,
  TextField, Stack, Menu, MenuItem, ListItemIcon, ListItemText, Switch
} from '@mui/material';
import { CustomTitle } from '../../common/CustomTitle/CustomTitle';
import AdbIcon from '@mui/icons-material/Adb';

import FilterListIcon from '@mui/icons-material/FilterList';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import moment from 'moment';
import { useLogs } from '../../../provider/LogsContextProvider';

export const LogScreenRealTime = ({ onClose }) => {
  // Estado global (Context)
  const { logs: logQueue, paused, clear, togglePause } = useLogs();

  // Estado local (solo visual)
  const [maxLogs, setMaxLogs] = useState(10); // 10–100

  // Filtros de campos
  const [showLevel, setShowLevel] = useState(true);
  const [showTime, setShowTime] = useState(true);
  const [showPid, setShowPid] = useState(true);
  const [showMsg, setShowMsg] = useState(true);
  const [compactView, setCompactView] = useState(false);

  // Menú de filtros
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);
  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  // Slice visible (los demás siguen guardados en el Context hasta HARD_CAP)
  const visibleLogs = logQueue.slice(-maxLogs);

  const getBackgroundColor = (content) => {
    if (content?.level === 'error') return '#ffebee';
    return '#ecfcdfff';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  const handleChangeMax = (e) => {
    const v = Number(e.target.value);
    if (Number.isNaN(v)) return;
    const clamped = Math.max(10, Math.min(100, v));
    setMaxLogs(clamped);
  };

  const copyJSON = async () => {
    const data = visibleLogs.map(l => l.content);
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    } catch (err) {
      console.error('Clipboard error:', err);
    }
  };

  const downloadJSON = () => {
    const data = visibleLogs.map(l => l.content);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'logs.json'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth={false} sx={{ p: { xs: 1, sm: 2 } }}>
      <Grid container spacing={{ xs: 1, sm: 2 }}>
        <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center' }}>
          <CustomTitle
            title="Logs en tiempo real"
            icon={<Avatar><AdbIcon /></Avatar>}
            advanced={true}
          />
        </Grid>

        <Grid item xs={12} md={7} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, }}>
          <Stack
            direction="row"
            spacing={{ xs: 0.5, sm: 1 }}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{ width: '100%', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
          >
            <Tooltip title={paused ? 'Reanudar' : 'Pausar'}>
              <IconButton onClick={togglePause} color={paused ? 'warning' : 'primary'}>
                {paused ? <PlayArrowIcon /> : <PauseIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Limpiar">
              <IconButton onClick={clear} color="error">
                <DeleteSweepIcon />
              </IconButton>
            </Tooltip>

            <TextField
              label="Max"
              size="small"
              type="number"
              value={maxLogs}
              onChange={handleChangeMax}
              inputProps={{ min: 10, max: 100 }}
              sx={{ width: 96 }}
            />

            <Tooltip title="Copiar JSON visible">
              <IconButton onClick={copyJSON}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Descargar JSON visible">
              <IconButton onClick={downloadJSON}>
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Filtros">
              <IconButton onClick={openMenu}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>


            <Menu
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={closeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => setShowLevel(v => !v)}
              >
                <ListItemIcon>{showLevel ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText>Level</ListItemText>
                <Switch
                  edge="end"
                  checked={showLevel}
                  onChange={(e) => setShowLevel(e.target.checked)}
                  onClick={(e) => e.stopPropagation()} // evita doble toggle
                />
              </MenuItem>

              <MenuItem
                onClick={() => setShowTime(v => !v)}
              >
                <ListItemIcon>{showTime ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText>Time</ListItemText>
                <Switch
                  edge="end"
                  checked={showTime}
                  onChange={(e) => setShowTime(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
              </MenuItem>

              <MenuItem
                onClick={() => setShowPid(v => !v)}
              >
                <ListItemIcon>{showPid ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText>PID</ListItemText>
                <Switch
                  edge="end"
                  checked={showPid}
                  onChange={(e) => setShowPid(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
              </MenuItem>

              <MenuItem
                onClick={() => setShowMsg(v => !v)}
              >
                <ListItemIcon>{showMsg ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText>Message</ListItemText>
                <Switch
                  edge="end"
                  checked={showMsg}
                  onChange={(e) => setShowMsg(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => setCompactView(v => !v)}
              >
                <ListItemIcon>{compactView ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText>Vista compacta</ListItemText>
                <Switch
                  edge="end"
                  checked={compactView}
                  onChange={(e) => setCompactView(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
              </MenuItem>
            </Menu>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          {visibleLogs.map((log, i) => {
            const content = log.content;
            const bgColor = getBackgroundColor(content);

            if (compactView) {
              const parts = [];
              if (showLevel) parts.push(content?.level);
              if (showTime) parts.push(formatTime(content?.time));
              if (showPid) parts.push(`pid:${content?.pid}`);
              if (showMsg) parts.push((content?.msg ?? content)?.toString());

              return (
                <Box
                  key={i}
                  sx={{
                    mb: 1, px: 1.5, py: 1, bgcolor: bgColor, borderRadius: 1.5,
                    fontFamily: 'monospace', fontSize: '0.85rem',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                  }}
                  title={parts.join(' | ')}
                >
                  <Typography variant="caption" sx={{ opacity: 0.7, mr: 1 }}>{log.topic}</Typography>
                  {parts.join(' | ')}
                </Box>
              );
            }

            return (
              <Box
                key={i}
                sx={{
                  mb: 2, p: 2, bgcolor: bgColor, borderRadius: 2,
                  fontFamily: 'monospace', fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '100%',
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {log.topic}
                </Typography>

                {showLevel && (
                  <Typography variant="body2"><b>Level:</b> {content?.level}</Typography>
                )}
                {showTime && (
                  <Typography variant="body2"><b>Time:</b> {formatTime(content?.time)}</Typography>
                )}
                {showPid && (
                  <Typography variant="body2"><b>PID:</b> {content?.pid}</Typography>
                )}
                {showMsg && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    <b>Message:</b> {content?.msg || (typeof content === 'string' ? content : JSON.stringify(content))}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Grid>
      </Grid>
    </Container>
  );
};
